import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

export type VisionTimeframe = '1-year' | '3-year' | '5-year' | '10-year' | 'lifetime';

export interface VisionEntry {
  id: string;
  life_area_id: string;
  timeframe: VisionTimeframe;
  vision_text: string;
  created_at: string;
  updated_at: string;
}

interface VisionState {
  visions: VisionEntry[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface VisionContextValue extends VisionState {
  initialize: () => Promise<void>;
  saveVision: (lifeAreaId: string, timeframe: VisionTimeframe, text: string) => Promise<void>;
  deleteVision: (id: string) => Promise<void>;
  getVisionsByArea: (lifeAreaId: string) => VisionEntry[];
  getVisionsByTimeframe: (timeframe: VisionTimeframe) => VisionEntry[];
  clear: () => void;
}

const VisionContext = createContext<VisionContextValue | null>(null);

export function VisionProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<VisionState>({
    visions: [],
    loading: false,
    error: null,
    initialized: false,
  });

  const encryptText = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptText = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return decrypt(crypto.encryptionKey, encrypted);
  };

  const initialize = async () => {
    // Don't initialize if encryption key isn't ready yet
    if (!crypto.encryptionKey) {
      console.log('Waiting for encryption key...');
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.get<{ visions: VisionEntry[] }>('/vision');

      const decryptedVisions = await Promise.all(
        result.visions.map(async (v) => ({
          ...v,
          vision_text: await decryptText(v.vision_text),
        }))
      );

      setState({
        visions: decryptedVisions,
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load visions',
        initialized: true, // Prevent infinite retry loops
      }));
    }
  };

  const saveVision = async (lifeAreaId: string, timeframe: VisionTimeframe, text: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedText = await encryptText(text);
      const result = await api.post<{ vision: VisionEntry }>('/vision', {
        life_area_id: lifeAreaId,
        timeframe,
        vision_text: encryptedText,
      });

      const decryptedVision = {
        ...result.vision,
        vision_text: await decryptText(result.vision.vision_text),
      };

      setState(s => ({
        ...s,
        visions: [
          ...s.visions.filter(v => !(v.life_area_id === lifeAreaId && v.timeframe === timeframe)),
          decryptedVision,
        ],
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to save vision',
      }));
    }
  };

  const deleteVision = async (id: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      await api.delete(`/vision/${id}`);
      setState(s => ({
        ...s,
        visions: s.visions.filter(v => v.id !== id),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to delete vision',
      }));
    }
  };

  const getVisionsByArea = (lifeAreaId: string) => {
    return state.visions.filter(v => v.life_area_id === lifeAreaId);
  };

  const getVisionsByTimeframe = (timeframe: VisionTimeframe) => {
    return state.visions.filter(v => v.timeframe === timeframe);
  };

  const clear = () => {
    setState({
      visions: [],
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const value: VisionContextValue = {
    ...state,
    initialize,
    saveVision,
    deleteVision,
    getVisionsByArea,
    getVisionsByTimeframe,
    clear,
  };

  return <VisionContext.Provider value={value}>{children}</VisionContext.Provider>;
}

export function useVision() {
  const context = useContext(VisionContext);
  if (!context) {
    throw new Error('useVision must be used within VisionProvider');
  }
  return context;
}
